/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PRESENTATION } from "../graphql/getAdminPresentationList.query";
import { VApplication } from "../../../application";

const is = require("is_js");
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IAdminPresentationListsMethods extends IPluginMethodMap {
  getAdminPresentationListsProps(args: any): Promise<any>;
}

export class AdminPresentationListsService implements IAgentPlugin {
  readonly methods: IAdminPresentationListsMethods;

  constructor() {
    this.methods = {
      getAdminPresentationListsProps:
        this.getAdminPresentationListsProps.bind(this),
    };
  }

  async getAdminPresentationListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_PRESENTATION,
          variables: {
            filter: {
              status: {
                eq: "active",
              },
            },
            options: {
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              sort: {
                field: "status",
                order: "asc",
              },
            },
          },
          fetchPolicy: "network-only",
        });

        if (
          res?.data?.getPresentations?.content &&
          is.not.empty(res?.data?.getPresentations?.content)
        ) {
          for (let obj of res?.data?.getPresentations?.content) {
            if (obj && is.not.empty(obj)) {
              const payload = this.formatData(obj);
              obj.sections = payload;
            }
          }

          if (
            res?.data?.getPresentations?.content &&
            is.not.empty(res?.data?.getPresentations?.content) &&
            !res?.data?.getPresentations?.content.some((e: any) => e.id === "")
          ) {
            res?.data?.getPresentations?.content.unshift({
              name: "...",
              id: "",
              sections: [
                {
                  name: "...",
                  id: "",
                  costumeTypes: [
                    {
                      name: "...",
                      id: "",
                      partition: [{ name: "...", id: "" }],
                    },
                  ],
                },
              ],
            });
          }

          return res?.data?.getPresentations;
        } else {
          return [];
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatData(data: any) {
    let sectionList: any = [
      {
        name: "...",
        id: "",
        costumeTypes: [
          { name: "...", id: "", partition: [{ name: "...", id: "" }] },
        ],
      },
    ];

    for (let obj of data?.sections) {
      if (obj && is.not.empty(obj)) {
        let costumeList: any = [
          { name: "...", id: "", partition: [{ name: "...", id: "" }] },
        ];

        for (let costumeObj of obj?.costumeTypes) {
          let partitionList: any = [{ name: "...", id: "" }];
          if (costumeObj && is.not.empty(costumeObj)) {
            if (
              !partitionList.some((e: any) => e.id === "default")
            ) {
              partitionList.push({
                name: "Default Invite",
                id: "default",
              });
            }

            if (
              costumeObj?.inventoryDetail?.sectionSpecificInvitesRemaining &&
              costumeObj?.inventoryDetail?.sectionSpecificInvitesRemaining >
                0 &&
              !partitionList.some((e: any) => e.id === "sectionSpecific")
            ) {
              partitionList.push({
                name: "Section Specific Invite",
                id: "sectionSpecific",
              });
            }

            if (
              costumeObj?.inventoryDetail?.specialInvitesRemaining &&
              costumeObj?.inventoryDetail?.specialInvitesRemaining > 0 &&
              !partitionList.some((e: any) => e.id === "special")
            ) {
              partitionList.push({
                name: "Special Invite",
                id: "special",
              });
            }

            costumeObj.partition = partitionList;

            if (costumeObj?.isVisible) {
              costumeList.push(costumeObj);
            }
          }
        }
        obj.costumeTypes = costumeList;

        if (obj?.isVisible) {
          sectionList.push(obj);
        }
      }
    }

    return sectionList;
  }
}
